import { logDebug, logError, logWarn } from '@ptek/client-logger';
import { WHISPER_CONFIG } from '../config/whisper';
import { useRecordingStore } from '../stores';
import {
  TranscriptMessage,
  TranscriptSnapshotMessage,
  WebSocketMessage,
} from '../types/recorder';
import type { SessionWithDocuments } from './sessions';

type WhisperConfig = typeof WHISPER_CONFIG;
const MAX_BUFFERED_TRANSCRIPT_SNAPSHOTS = 8;

/**
 * Global auth manager for API clients
 * This allows React components to set the token getter and API clients to access it
 */
class AuthManager {
  private static instance: AuthManager | null = null;
  private tokenGetter: (() => Promise<string | null>) | null = null;

  public static getInstance(): AuthManager {
    if (!AuthManager.instance) {
      AuthManager.instance = new AuthManager();
    }
    return AuthManager.instance;
  }

  public setTokenGetter(getter: () => Promise<string | null>): void {
    this.tokenGetter = getter;
  }

  public async getToken(): Promise<string | null> {
    if (!this.tokenGetter) {
      return null;
    }
    try {
      return await this.tokenGetter();
    } catch (error) {
      logError('WhisperClient', 'Failed to get token:', error);
      return null;
    }
  }
}

export const authManager = AuthManager.getInstance();

/**
 * Chunk-upload rejection carrying the HTTP status when a response was
 * received; `status` is undefined for network errors and timeouts. The upload
 * queue classifies retryability from it (4xx other than 408/429 can never
 * succeed on replay; everything else is transient).
 *
 * `code` is the response body's machine-readable code when the body carried
 * one; it disambiguates same-status failures (the route answers 409 both for a
 * rival recorder lease and for continuous capture missing authentication).
 */
export class ChunkUploadError extends Error {
  constructor(
    message: string,
    public readonly status?: number,
    public readonly code?: string
  ) {
    super(message);
    this.name = 'ChunkUploadError';
  }
}

/**
 * Never throws: an unreadable, empty, non-JSON, or code-less body yields
 * undefined, so a malformed error response degrades to status-only
 * classification rather than failing the upload for a different reason.
 */
function parseChunkUploadErrorCode(xhr: XMLHttpRequest): string | undefined {
  try {
    const body = xhr.responseText;
    if (typeof body !== 'string' || body === '') {
      return undefined;
    }
    const code = (JSON.parse(body) as { code?: unknown } | null)?.code;
    return typeof code === 'string' ? code : undefined;
  } catch {
    return undefined;
  }
}

/**
 * The silence verdict in a 200 upload body: true when the server skipped the
 * chunk as silent, false for any other JSON object, and undefined (no verdict)
 * for an empty or unparseable body. Never throws.
 */
function parseChunkSilentVerdict(xhr: XMLHttpRequest): boolean | undefined {
  try {
    const body = xhr.responseText;
    if (typeof body !== 'string' || body === '') {
      return undefined;
    }
    const parsed = JSON.parse(body) as { silent?: unknown } | null;
    if (typeof parsed !== 'object' || parsed === null) {
      return undefined;
    }
    return parsed.silent === true;
  } catch {
    return undefined;
  }
}

/**
 * WhisperClient - Handles WebSocket connection and audio chunk uploads
 * Implemented as a singleton to maintain a persistent connection across component mounts
 */
export class WhisperClient {
  private static instance: WhisperClient | null = null;
  private ws: WebSocket | null = null;
  private sessionId: string | null = null;
  private config: WhisperConfig;
  private pingInterval: number | null = null;
  private connectionAttemptInProgress: boolean = false;
  private connectionPromise: Promise<void> | null = null;
  /** Set from initSession's start until its initUpload reply/error settles. */
  private initSessionInFlight = false;
  private reconnectAttempts: number = 0;
  private reconnectTimeout: number | null = null;
  private transcriptSnapshotVersion: number | null = null;
  private snapshotFrames = new Map<
    string,
    {
      message: Omit<TranscriptSnapshotMessage, 'transcript'>;
      parts: Array<string | undefined>;
    }
  >();

  private resetTranscriptSnapshotState(): void {
    this.transcriptSnapshotVersion = null;
    this.snapshotFrames.clear();
  }

  public onTranscript?: (transcript: TranscriptMessage) => void;
  public onTranscriptSnapshot?: (snapshot: TranscriptSnapshotMessage) => void;
  public onSessionInitialized?: (sessionId: string) => void;
  public onConnectionChange?: (
    status: 'connected' | 'disconnected' | 'connecting' | 'error'
  ) => void;
  public onError?: (error: string) => void;
  public onDocumentGenerated?: (data: SessionWithDocuments) => void;

  constructor(config: WhisperConfig) {
    this.config = config;
  }

  /**
   * Get singleton instance of WhisperClient
   */
  public static getInstance(config?: WhisperConfig): WhisperClient {
    if (!WhisperClient.instance) {
      WhisperClient.instance = new WhisperClient(config || WHISPER_CONFIG);
    }
    return WhisperClient.instance;
  }

  /**
   * Reset the singleton instance (for cleanup)
   */
  public static resetInstance(): void {
    if (WhisperClient.instance) {
      WhisperClient.instance.disconnect();
      WhisperClient.instance = null;
    }
  }

  /**
   * Connect to the WebSocket server
   * @param wsUrl - Optional WebSocket URL override.
   */
  public async connect(wsUrl?: string): Promise<void> {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.onConnectionChange?.('connected');
      return;
    }

    // If connection is in progress, return the existing promise
    if (this.connectionAttemptInProgress && this.connectionPromise) {
      return this.connectionPromise;
    }

    this.connectionAttemptInProgress = true;

    this.onConnectionChange?.('connecting');

    let url = wsUrl || this.config.websocketUrl;

    if (!url) {
      const errorMessage =
        'WhisperClient: WebSocket URL not configured. Please set VITE_WHISPER_WS_URL environment variable.';
      logError('WhisperClient', errorMessage);
      this.onConnectionChange?.('error');
      this.onError?.(errorMessage);
      throw new Error(errorMessage);
    }

    const token = await authManager.getToken();
    if (token) {
      const separator = url.includes('?') ? '&' : '?';
      url = `${url}${separator}token=${encodeURIComponent(token)}`;
    } else {
      logWarn(
        'WhisperClient',
        'No authentication token available for WebSocket connection'
      );
    }

    // Quick pre-check: verify the WebSocket URL is at least syntactically valid
    try {
      new URL(url);
    } catch {
      const errorMessage = `Invalid WebSocket URL: ${url}`;
      this.onConnectionChange?.('error');
      this.onError?.(errorMessage);
      this.connectionAttemptInProgress = false;
      throw new Error(errorMessage);
    }

    // Log connection attempts at debug level to reduce noise
    if (this.reconnectAttempts === 0) {
      logDebug(
        'WhisperClient',
        'Attempting to connect to:',
        url.replace(/token=[^&]+/, 'token=***')
      );
    } else {
      logDebug(
        'WhisperClient',
        `Retry attempt ${this.reconnectAttempts + 1}/${this.config.maxReconnectAttempts}`
      );
    }

    this.connectionPromise = new Promise<void>((resolve, reject) => {
      const connectionTimeout = setTimeout(() => {
        if (this.ws && this.ws.readyState !== WebSocket.OPEN) {
          this.ws.onopen = null;
          this.ws.onerror = null;
          this.ws.onclose = null;
          this.ws.close();
          this.ws = null;

          this.onConnectionChange?.('error');
          this.onError?.(`Connection timed out (URL: ${url})`);
          this.connectionAttemptInProgress = false;
          reject(new Error('WebSocket connection timed out'));
        }
      }, this.config.reconnectDelayMs * 3); // 3x the reconnect delay as timeout

      try {
        if (this.ws) {
          this.ws.onclose = null; // Prevent triggering reconnect logic
          this.ws.close();
          this.ws = null;
        }

        this.ws = new WebSocket(url);

        this.ws.onopen = () => {
          clearTimeout(connectionTimeout);
          this.connectionAttemptInProgress = false;
          this.onConnectionChange?.('connected');

          this.startPingInterval();

          resolve();
        };

        this.ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            this.handleMessage(data);
          } catch (error) {
            logError(
              'WhisperClient',
              'Error parsing WebSocket message:',
              error
            );
          }
        };

        this.ws.onerror = () => {
          // Only handle error if we're still waiting for a connection
          if (this.ws && this.ws.readyState !== WebSocket.OPEN) {
            clearTimeout(connectionTimeout);
            this.connectionAttemptInProgress = false;

            const errorMessage = `WebSocket connection error (URL: ${url})`;
            this.onConnectionChange?.('error');

            // Only call onError for persistent failures (after retries), not initial attempts
            if (this.reconnectAttempts >= this.config.maxReconnectAttempts) {
              this.onError?.(errorMessage);
            }

            reject(new Error(errorMessage));
          }
        };

        this.ws.onclose = (event) => {
          clearTimeout(connectionTimeout);
          this.stopPingInterval();

          // Only update state if this is our current WebSocket
          this.onConnectionChange?.('disconnected');

          const isAbnormalClosure = event.code === 1006;
          const isNormalClosure = event.code === 1000 || event.code === 1001;

          if (!isNormalClosure) {
            const closeReason = event.reason || `Code: ${event.code}`;
            const errorMessage = `WebSocket connection closed: ${closeReason}`;

            // For code 1006 (abnormal closure), attempt reconnection
            if (
              isAbnormalClosure &&
              this.reconnectAttempts < this.config.maxReconnectAttempts
            ) {
              logWarn(
                'WhisperClient',
                `${errorMessage} - Attempting reconnection (${this.reconnectAttempts + 1}/${this.config.maxReconnectAttempts})`
              );
              this.scheduleReconnect();
            } else {
              this.onError?.(errorMessage);
            }

            // If connection was still in progress, reject the promise
            if (this.connectionAttemptInProgress) {
              this.connectionAttemptInProgress = false;
              reject(new Error(errorMessage));
            }
          } else if (this.connectionAttemptInProgress) {
            this.connectionAttemptInProgress = false;
            reject(
              new Error('Connection closed normally before it was established')
            );
          }
        };
      } catch (error: unknown) {
        clearTimeout(connectionTimeout);
        this.connectionAttemptInProgress = false;
        this.onConnectionChange?.('error');

        const errorMessage = `Failed to create WebSocket connection: ${error instanceof Error ? error.message : String(error)}`;
        this.onError?.(errorMessage);

        reject(new Error(errorMessage));
      }
    });

    return this.connectionPromise;
  }

  /**
   * Initialize a new recording session
   */
  public async initSession(): Promise<string> {
    this.initSessionInFlight = true;
    try {
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
        const maxAttempts = 3;
        for (let attempt = 1; attempt <= maxAttempts; attempt++) {
          try {
            await this.connect();
            break;
          } catch (error) {
            logWarn(
              'WhisperClient',
              `Connection attempt ${attempt} of ${maxAttempts} failed.`
            );
            if (attempt === maxAttempts) {
              logError(
                'WhisperClient',
                'Failed to connect to WebSocket server after all attempts.',
                error
              );
              throw new Error('Failed to connect to WebSocket server');
            }
            await new Promise((res) => setTimeout(res, 500 * attempt));
          }
        }
      }

      return await new Promise<string>((resolve, reject) => {
        let settled = false;
        const originalHandler = this.onSessionInitialized;
        const originalErrorHandler = this.onError;
        const timeout = setTimeout(() => {
          settled = true;
          this.onSessionInitialized = originalHandler;
          this.onError = originalErrorHandler;
          reject(new Error('Session initialization timeout'));
        }, 10000);

        const settleError = (message: string) => {
          if (settled) return;
          settled = true;
          clearTimeout(timeout);
          this.onSessionInitialized = originalHandler;
          this.onError = originalErrorHandler;
          reject(new Error(message));
        };

        this.onSessionInitialized = (sessionId: string) => {
          if (settled) return;
          settled = true;
          clearTimeout(timeout);
          this.onSessionInitialized = originalHandler;
          this.onError = originalErrorHandler;
          resolve(sessionId);
        };
        this.onError = (message: string) => {
          originalErrorHandler?.(message);
          settleError(message || 'Unknown server error');
        };

        const { unifiedSessionIdEnabled, recordingSessionId } =
          useRecordingStore.getState();

        if (unifiedSessionIdEnabled && recordingSessionId) {
          this.resetTranscriptSnapshotState();
          this.sendMessage({
            action: 'initUpload',
            sessionId: recordingSessionId,
            transcriptSnapshotVersion: 1,
          });
        } else {
          this.sendMessage({ action: 'initUpload' });
        }
      });
    } finally {
      this.initSessionInFlight = false;
    }
  }

  /**
   * Subscribe the current WebSocket connection to live updates for a durable
   * docgen session id. The server resolves the connection's whisper id from the
   * durable id and registers it under the SessionIdIndex so the worker's
   * completion push (onDocumentGenerated) reaches this reopened connection.
   * Mirrors the initUpload sendMessage usage.
   * @param durableId - The durable document-generation session id.
   */
  public subscribe(durableId: string): void {
    this.sendMessage({ action: 'subscribe', sessionId: durableId });
  }

  /**
   * Upload an audio chunk to the REST API
   * @param chunkNumber - The chunk number
   * @param blob - The audio blob to upload
   * @param isFinal - Whether this is the final chunk
   * @param takeStart - First chunk number of the current take (NEB-100035).
   *   Sent only on the final chunk so the server knows where the latest take
   *   begins across monotonic re-record numbering. Ignored server-side for
   *   flag-off sessions.
   * @param captureAgeMs - Age of the chunk's audio at upload time
   *   (now − capture end, this client's clock on both sides so clock skew
   *   cancels). The server stamps capture-time liveness from it. Sent only by
   *   the continuous-capture path, where buffered chunks can upload long after
   *   capture.
   * @param sessionId - Upload-session override. The upload queue pins each
   *   chunk to the session it was captured under, so a chunk still draining
   *   after a new session was initialized never uploads to the wrong session.
   *   Defaults to the client's current session.
   * @param leaseId - The recorder lease this run holds. Sent only on unified
   *   uploads, where the server fences the chunk against the session's active
   *   lease and answers 409 rather than letting a second computer overwrite an
   *   existing chunk. Absent for sessions that never took a lease.
   * @returns The server's silence verdict: true when it skipped the chunk as
   *   silent, false when it sent it for transcription, undefined when the 200
   *   body carried no verdict.
   */
  public async uploadChunk(
    chunkNumber: number,
    blob: Blob,
    isFinal = false,
    takeStart?: number,
    unifiedStitching = false,
    takeGeneration?: number,
    captureAgeMs?: number,
    sessionId?: string,
    leaseId?: string
  ): Promise<boolean | undefined> {
    const uploadSessionId = sessionId ?? this.sessionId;
    if (!uploadSessionId) {
      throw new Error('Session not initialized');
    }

    if (!(blob instanceof Blob)) {
      throw new Error(`Invalid blob: not a Blob object`);
    }

    if (blob.size < this.config.minChunkSizeBytes) {
      throw new Error(
        `Invalid blob: size=${blob.size}, type=${blob.type}, minimum size=${this.config.minChunkSizeBytes}`
      );
    }

    const query = new URLSearchParams();
    if (isFinal) query.set('final', 'true');
    if (takeStart !== undefined) query.set('takeStart', String(takeStart));
    if (unifiedStitching) {
      if (takeGeneration === undefined) {
        throw new Error('takeGeneration is required for unified stitching');
      }
      query.set('unifiedStitching', 'true');
      query.set('takeGeneration', String(takeGeneration));
      if (leaseId) {
        query.set('leaseId', leaseId);
      }
    }
    if (captureAgeMs !== undefined) {
      query.set('captureAgeMs', String(captureAgeMs));
    }
    const queryString = query.toString();
    const uploadUrl = `${this.config.restApiUrl}/uploads/${uploadSessionId}/chunks/${chunkNumber}${queryString ? `?${queryString}` : ''}`;

    const attemptUpload = () =>
      new Promise<boolean | undefined>((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        xhr.timeout = this.config.uploadTimeoutMs;

        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            // Progress tracking without logging
          }
        };

        xhr.onload = () => {
          if (xhr.status === 200) return resolve(parseChunkSilentVerdict(xhr));
          reject(
            new ChunkUploadError(
              `Upload failed: ${xhr.status} ${xhr.statusText}`,
              xhr.status,
              parseChunkUploadErrorCode(xhr)
            )
          );
        };

        xhr.onerror = () => {
          reject(new ChunkUploadError('Upload failed due to network error'));
        };

        xhr.ontimeout = () => {
          reject(new ChunkUploadError('Upload timed out'));
        };

        const setupRequest = async () => {
          try {
            xhr.open('POST', uploadUrl);
            xhr.setRequestHeader('Content-Type', 'audio/webm');
            xhr.setRequestHeader('X-Client-Id', 'ambient-scribe-widget');

            const token = await authManager.getToken();
            if (token) {
              xhr.setRequestHeader('Authorization', `Bearer ${token}`);
            }

            xhr.send(blob);
          } catch (error) {
            logError('WhisperClient', 'Error setting up XHR request:', error);
            reject(error);
          }
        };

        setupRequest();
      });

    return attemptUpload();
  }

  /**
   * Re-establish the WebSocket transport after a connectivity recovery
   * (browser `online`): the automatic reconnects cap at
   * maxReconnectAttempts, so a long outage exhausts them and nothing else
   * would reopen the socket. Transport/liveness only — re-registering the
   * durable session lets transcript pushes resume, but does NOT restore any
   * server-side sweep state cleared during the outage.
   */
  public async reconnectTransport(): Promise<void> {
    if (this.isConnected()) {
      return;
    }
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    this.reconnectAttempts = 0;
    await this.connect();
    this.reRegisterUploadSession();
  }

  /**
   * Re-register a freshly reconnected socket. Without it the new socket has no
   * sessionId on its connections-table row and every transcript push is
   * dropped server-side. Unified: re-asserts the durable id (idempotent) and
   * re-negotiates the snapshot protocol — omitting it would leave snapshots
   * disabled. Otherwise: a plain initUpload, mirroring initSession's
   * non-unified path; the server mints a new whisper session id, which the
   * initUpload reply adopts as this.sessionId for later uploads.
   *
   * Callers differ when the flag is on without a durable id: reconnectTransport
   * sends the plain initUpload; scheduleReconnect skips re-registration, since
   * a fresh id would register a socket no continuous-capture upload targets.
   * reconnectTransport is only reachable on the flag-on continuous path
   * (upload-queue `online`).
   */
  private reRegisterUploadSession(): void {
    const { unifiedSessionIdEnabled, recordingSessionId } =
      useRecordingStore.getState();
    if (unifiedSessionIdEnabled && recordingSessionId) {
      this.resetTranscriptSnapshotState();
      this.sendMessage({
        action: 'initUpload',
        sessionId: recordingSessionId,
        transcriptSnapshotVersion: 1,
      });
    } else {
      this.sendMessage({ action: 'initUpload' });
    }
  }

  /**
   * Disconnect from the WebSocket server
   */
  public disconnect(): void {
    this.stopPingInterval();

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    this.reconnectAttempts = 0;

    if (this.ws) {
      this.ws.onclose = null;
      this.ws.onerror = null;
      this.ws.onopen = null;
      this.ws.close();
      this.ws = null;
    }

    this.connectionAttemptInProgress = false;
    this.connectionPromise = null;
    this.resetTranscriptSnapshotState();

    // Don't clear sessionId here as we might need it for late uploads
  }

  /**
   * Check if connected
   */
  public isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  /**
   * Get current session ID
   */
  public getSessionId(): string | null {
    return this.sessionId;
  }

  /**
   * Get the WebSocket URL from config
   */
  public getWebSocketUrl(): string {
    return this.config.websocketUrl;
  }

  private handleMessage(data: WebSocketMessage): void {
    switch (data.action) {
      case 'initUpload':
        if (data.sessionId) {
          this.sessionId = data.sessionId;
          this.transcriptSnapshotVersion =
            data.transcriptSnapshotVersion === 1 ? 1 : null;
          this.onSessionInitialized?.(data.sessionId);
        }
        break;

      case 'transcript':
        try {
          const transcriptData = data as unknown as TranscriptMessage;

          if (this.onTranscript) {
            this.onTranscript(transcriptData);
          }
        } catch (error) {
          logError(
            'WhisperClient',
            'Error handling transcript message:',
            error
          );
        }
        break;

      case 'transcriptSnapshot':
        this.handleTranscriptSnapshotFrame(data);
        break;

      case 'documentGenerated':
        this.onDocumentGenerated?.(data.data as SessionWithDocuments);
        break;

      case 'error':
        this.onError?.(data.message || 'Unknown server error');
        break;

      case 'pong':
        break;

      default:
        break;
    }
  }

  private handleTranscriptSnapshotFrame(data: WebSocketMessage): void {
    if (
      this.transcriptSnapshotVersion !== 1 ||
      data.protocolVersion !== 1 ||
      typeof data.snapshotId !== 'string' ||
      typeof data.sessionId !== 'string' ||
      data.sessionId !== this.sessionId ||
      typeof data.takeGeneration !== 'number' ||
      typeof data.takeStartChunk !== 'number' ||
      typeof data.maxIncludedChunk !== 'number' ||
      typeof data.frameIndex !== 'number' ||
      typeof data.frameCount !== 'number' ||
      typeof data.transcriptPart !== 'string' ||
      typeof data.partial !== 'boolean' ||
      data.frameCount < 1 ||
      data.frameIndex < 0 ||
      data.frameIndex >= data.frameCount
    ) {
      return;
    }

    // An incomplete older snapshot can otherwise live forever. Reject frames
    // older than a buffered watermark and evict superseded buffers for this
    // session/take before accepting the new snapshot.
    for (const [snapshotId, entry] of this.snapshotFrames) {
      if (entry.message.sessionId !== data.sessionId) continue;
      const bufferedIsNewer =
        entry.message.takeGeneration > data.takeGeneration ||
        (entry.message.takeGeneration === data.takeGeneration &&
          entry.message.takeStartChunk === data.takeStartChunk &&
          entry.message.maxIncludedChunk > data.maxIncludedChunk);
      if (bufferedIsNewer) return;

      const incomingSupersedes =
        entry.message.takeGeneration < data.takeGeneration ||
        (entry.message.takeGeneration === data.takeGeneration &&
          entry.message.takeStartChunk === data.takeStartChunk &&
          entry.message.maxIncludedChunk < data.maxIncludedChunk);
      if (incomingSupersedes) this.snapshotFrames.delete(snapshotId);
    }

    let buffered = this.snapshotFrames.get(data.snapshotId);
    if (!buffered) {
      if (this.snapshotFrames.size >= MAX_BUFFERED_TRANSCRIPT_SNAPSHOTS) {
        const oldestSnapshotId = this.snapshotFrames.keys().next().value;
        if (typeof oldestSnapshotId === 'string') {
          this.snapshotFrames.delete(oldestSnapshotId);
        }
      }
      buffered = {
        message: {
          action: 'transcriptSnapshot',
          protocolVersion: 1,
          snapshotId: data.snapshotId,
          sessionId: data.sessionId,
          takeGeneration: data.takeGeneration,
          takeStartChunk: data.takeStartChunk,
          maxIncludedChunk: data.maxIncludedChunk,
          partial: data.partial,
        },
        parts: new Array<string | undefined>(data.frameCount).fill(undefined),
      };
      this.snapshotFrames.set(data.snapshotId, buffered);
    }

    if (buffered.parts.length !== data.frameCount) return;
    buffered.parts[data.frameIndex] = data.transcriptPart;
    if (buffered.parts.every((part) => typeof part === 'string')) {
      this.snapshotFrames.delete(data.snapshotId);
      this.onTranscriptSnapshot?.({
        ...buffered.message,
        transcript: buffered.parts.join(''),
      });
    }
  }

  private sendMessage(message: WebSocketMessage): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    }
  }

  private startPingInterval(): void {
    this.stopPingInterval();

    this.pingInterval = window.setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.sendMessage({ action: 'ping' });
      } else {
        this.stopPingInterval();
      }
    }, 30000);
  }

  private stopPingInterval(): void {
    if (this.pingInterval !== null) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    this.reconnectAttempts++;

    const baseDelay = this.config.reconnectDelayMs;
    const delay = baseDelay * Math.pow(2, this.reconnectAttempts - 1);

    this.reconnectTimeout = window.setTimeout(async () => {
      try {
        logDebug(
          'WhisperClient',
          `Attempting to reconnect (${this.reconnectAttempts}/${this.config.maxReconnectAttempts})...`
        );
        await this.connect();

        const { unifiedSessionIdEnabled, recordingSessionId } =
          useRecordingStore.getState();
        // Flag off: only restore an upload session the client had, and leave an
        // in-flight initSession's own initUpload as this socket's registration.
        const restoreLegacy =
          !unifiedSessionIdEnabled &&
          this.sessionId !== null &&
          !this.initSessionInFlight;
        // Flag on without a durable id has never re-registered here; keep it.
        if ((unifiedSessionIdEnabled && recordingSessionId) || restoreLegacy) {
          this.reRegisterUploadSession();
        }

        this.reconnectAttempts = 0;
        this.reconnectTimeout = null;
      } catch (error) {
        logError('WhisperClient', 'Reconnection attempt failed:', error);

        if (this.reconnectAttempts >= this.config.maxReconnectAttempts) {
          this.onError?.(
            `Failed to reconnect after ${this.config.maxReconnectAttempts} attempts`
          );
          this.reconnectAttempts = 0;
          this.reconnectTimeout = null;
        }
      }
    }, delay);
  }
}
